import { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import AddIdeaDialog from "@/components/AddIdeaDialog";
import ErrorBoundary from "@/components/ErrorBoundary";
import { BrandLogo } from "@/components/Brand";
import {
  LayoutGrid, Palette, Lightbulb, ClipboardList, Sparkles, BarChart3,
  CalendarClock, Settings as SettingsIcon, Plus, LogOut,
  Compass, MoreHorizontal, FolderKanban, Library, Building2, ShieldCheck, Home, Landmark, Contact, Mail,
} from "lucide-react";

export const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: Home },
  { to: "/content", label: "Content Command Center", icon: LayoutGrid },
  { to: "/brand", label: "Brand Center", icon: Palette },
  { to: "/ideas", label: "Idea Vault", icon: Lightbulb },
  { to: "/sops", label: "SOP Library", icon: ClipboardList },
  { to: "/guide", label: "AI Business Guide", icon: Sparkles },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/grants", label: "Grant Finder", icon: Landmark },
  { to: "/crm", label: "CRM", icon: Contact },
  { to: "/email", label: "Email", icon: Mail },
  { to: "/knowledge", label: "Knowledge Library", icon: Library },
  { to: "/business", label: "Business Center", icon: BarChart3 },
  { to: "/planning", label: "Planning", icon: CalendarClock },
  { to: "/setup", label: "Business Setup", icon: Building2 },
];

const MOBILE_PRIMARY = ["/dashboard", "/content", "/ideas", "/sops"];

function NavItem({ item, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      data-testid={`nav-${item.to.slice(1)}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors duration-300 ${
          isActive
            ? "bg-primary text-primary-foreground shadow-soft"
            : "text-foreground/70 hover:bg-muted hover:text-foreground"
        }`
      }
    >
      <Icon className="h-5 w-5 shrink-0" strokeWidth={1.75} />
      <span className="text-sm font-medium flex-1">{item.label}</span>
    </NavLink>
  );
}

function UserChip({ user, logout }) {
  const initials = (user?.name || "You").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="flex items-center gap-3 px-2 py-2 rounded-2xl">
      <Avatar className="h-9 w-9">
        <AvatarImage src={user?.picture} alt={user?.name} />
        <AvatarFallback className="bg-secondary/15 text-secondary text-xs">{initials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{user?.name || "Owner"}</p>
        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
      </div>
      <NavLink to="/settings" data-testid="nav-settings" className="p-2 rounded-full hover:bg-muted text-muted-foreground transition-colors" title="Settings">
        <SettingsIcon className="h-4 w-4" />
      </NavLink>
      <button onClick={logout} data-testid="logout-button" className="p-2 rounded-full hover:bg-muted text-muted-foreground transition-colors" title="Sign out">
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [ideaOpen, setIdeaOpen] = useState(false);
  const firstRunChecked = useRef(false);

  // Brand-new owners are guided to the setup wizard once per session.
  useEffect(() => {
    if (firstRunChecked.current) return;
    firstRunChecked.current = true;
    api.get("/business-setup")
      .then((r) => {
        if (r.data && r.data.completed === false && !window.location.pathname.startsWith("/setup")) {
          navigate("/setup");
        }
      })
      .catch(() => {});
  }, [navigate]);

  const primary = NAV.filter((n) => MOBILE_PRIMARY.includes(n.to));
  const secondary = NAV.filter((n) => !MOBILE_PRIMARY.includes(n.to));

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 flex-col border-r border-border bg-background/80 backdrop-blur-md px-4 py-6">
        <div className="flex items-center px-3 mb-6">
          <BrandLogo className="h-20 w-auto max-w-[240px] object-contain object-left" />
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto">
          {NAV.map((item) => <NavItem key={item.to} item={item} />)}
          {user?.is_admin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors duration-300 ${
                  isActive ? "bg-secondary text-secondary-foreground shadow-soft" : "text-foreground/70 hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <ShieldCheck className="h-5 w-5 shrink-0" strokeWidth={1.75} />
              <span className="text-sm font-medium flex-1">Customers</span>
            </NavLink>
          )}
        </nav>
        <div className="pt-4 border-t border-border mt-4">
          <UserChip user={user} logout={logout} />
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-5 h-[72px] bg-background/85 backdrop-blur-md border-b border-border">
        <div className="flex items-center gap-2.5">
          <BrandLogo className="h-14 w-auto max-w-[210px] object-contain object-left" />
        </div>
        <Avatar className="h-8 w-8">
          <AvatarImage src={user?.picture} alt={user?.name} />
          <AvatarFallback className="bg-secondary/15 text-secondary text-xs">
            {(user?.name || "J")[0]}
          </AvatarFallback>
        </Avatar>
      </header>

      {/* Main content */}
      <main className="lg:pl-72">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-8 pb-28 lg:pb-16 animate-gentle-fade" key={location.pathname}>
          <ErrorBoundary routeKey={location.pathname}>
            {children}
          </ErrorBoundary>
        </div>
      </main>

      {/* Quick-add FAB */}
      {!location.pathname.startsWith("/guide") && !location.pathname.startsWith("/projects") && !location.pathname.startsWith("/knowledge") && (
      <div className="fixed bottom-24 right-5 lg:bottom-8 lg:right-8 z-40">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="quick-add-fab"
              className="h-14 w-14 rounded-full bg-secondary text-secondary-foreground shadow-soft-lg flex items-center justify-center hover:scale-[1.04] transition-transform duration-300"
            >
              <Plus className="h-6 w-6" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-2xl w-56">
            <DropdownMenuItem onClick={() => setIdeaOpen(true)} data-testid="quick-add-idea" className="rounded-xl cursor-pointer py-2.5">
              <Lightbulb className="h-4 w-4 mr-2" /> New idea
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/content")} data-testid="quick-add-content" className="rounded-xl cursor-pointer py-2.5">
              <LayoutGrid className="h-4 w-4 mr-2" /> Content Command Center
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      )}

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1 px-2 py-2 rounded-full bg-white/80 backdrop-blur-xl border border-border shadow-soft-lg">
        {primary.map((item) => {
          const Icon = item.icon;
          const active = location.pathname.startsWith(item.to);
          return (
            <NavLink key={item.to} to={item.to} data-testid={`mobilenav-${item.to.slice(1)}`}
              className={`flex flex-col items-center justify-center h-12 w-14 rounded-full transition-colors duration-300 ${active ? "bg-primary text-primary-foreground" : "text-foreground/60"}`}>
              <Icon className="h-5 w-5" strokeWidth={1.75} />
            </NavLink>
          );
        })}
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobilenav-more" className="flex flex-col items-center justify-center h-12 w-14 rounded-full text-foreground/60">
              <MoreHorizontal className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-3xl pb-8">
            <SheetTitle className="sr-only">More sections</SheetTitle>
            <SheetDescription className="sr-only">Navigate to more sections of your workspace.</SheetDescription>
            <div className="grid grid-cols-1 gap-1 mt-4">
              {secondary.map((item) => <NavItem key={item.to} item={item} onClick={() => setMoreOpen(false)} />)}
              <div className="pt-3 mt-2 border-t border-border">
                <UserChip user={user} logout={logout} />
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </nav>

      <AddIdeaDialog open={ideaOpen} onOpenChange={setIdeaOpen} onSaved={() => window.dispatchEvent(new Event("sfos:refresh"))} />
    </div>
  );
}
